import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from './services/api';
import LoginScreen from './components/LoginScreen';
import Dashboard from './components/Dashboard';
import VolunteerTable from './components/VolunteerTable';
import EntityManagement from './components/EntityManagement';
import RondasManagement from './components/RondasManagement';
import LocaisManagement from './components/LocaisManagement';
import AdminManagement from './components/AdminManagement';
import EventsDashboard from './components/EventsDashboard';

function App() {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // App state
  const [activeTab, setActiveTab] = useState('dashboard');
  const [volunteers, setVolunteers] = useState([]);
  const [entities, setEntities] = useState([]);
  const [rondas, setRondas] = useState([]);
  const [locais, setLocais] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Fetch all data
  const fetchData = useCallback(async () => {
    if (loading) return; // Prevent concurrent fetches
    setLoading(true);
    setError(null);

    try {
      // Base requests for all admin types
      const baseRequests = [
        adminApi.getVolunteers(),
        adminApi.getEntities(),
        adminApi.getRondas(),
        adminApi.getStats(),
        adminApi.getLocais()
      ];

      // Add admin request for master admins only
      const isMasterAdmin = currentUser?.tipo === 'mestre';
      if (isMasterAdmin) {
        baseRequests.push(adminApi.getAdmins());
      }

      const responses = await Promise.all(baseRequests);

      // All these responses have data in .data.data format
      setVolunteers(responses[0].data?.data && Array.isArray(responses[0].data.data) ? responses[0].data.data : []);
      setEntities(responses[1].data?.data && Array.isArray(responses[1].data.data) ? responses[1].data.data : []);
      setRondas(responses[2].data?.data && Array.isArray(responses[2].data.data) ? responses[2].data.data : []);

      // Stats response has data in .data format (not .data.data)
      console.log('Stats response:', responses[3].data);
      setStats(responses[3].data?.data || responses[3].data || {});

      setLocais(responses[4].data?.data && Array.isArray(responses[4].data.data) ? responses[4].data.data : []);

      // Set admins only if master admin
      if (isMasterAdmin && responses[5]) {
        setAdmins(Array.isArray(responses[5].data.data) ? responses[5].data.data : []);
      } else {
        setAdmins([]);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Check authentication on app load
  useEffect(() => {
    const checkAuth = () => {
      setAuthLoading(true);
      
      if (adminApi.isAuthenticated()) {
        const user = adminApi.getCurrentUser();
        if (user) {
          setIsAuthenticated(true);
          setCurrentUser(user);
          // Data will be loaded by the useEffect below
        } else {
          // Token exists but no user data, clear and show login
          adminApi.clearAuth();
          setIsAuthenticated(false);
          setCurrentUser(null);
        }
      } else {
        setIsAuthenticated(false);
        setCurrentUser(null);
      }
      
      setAuthLoading(false);
    };

    checkAuth();
  }, []); // Run only once on mount

  // Only fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  // Auto-refresh every 2 minutes (only when authenticated and not editing)
  useEffect(() => {
    if (isAuthenticated && !isEditing) {
      const interval = setInterval(fetchData, 120000); // 2 minutes instead of 30 seconds
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, isEditing]);

  // Authentication handlers
  const handleLoginSuccess = (user, tokens) => {
    setCurrentUser(user);
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    try {
      await adminApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsAuthenticated(false);
      setCurrentUser(null);
      // Clear all app data
      setVolunteers([]);
      setEntities([]);
      setRondas([]);
      setLocais([]);
      setAdmins([]);
      setStats(null);
      setActiveTab('dashboard');
    }
  };

  // Handle volunteer approval
  const handleApproveVolunteer = async (email, approvalData) => {
    try {
      await adminApi.approveVolunteer(email, approvalData);
      await fetchData(); // Refresh data

      // Show appropriate message based on action
      if (approvalData.approved) {
        alert('Voluntário aprovado com sucesso!');
      } else {
        alert('Voluntário rejeitado e eliminado do sistema com sucesso!');
      }
    } catch (error) {
      throw error;
    }
  };

  // Handle volunteer status update
  const handleUpdateVolunteerStatus = async (email, statusData) => {
    try {
      await adminApi.updateVolunteerStatus(email, statusData);
      await fetchData(); // Refresh data
      alert('Status do voluntário atualizado com sucesso!');
    } catch (error) {
      throw error;
    }
  };

  // Entity management handlers
  const handleCreateEntity = async (entityData) => {
    try {
      await adminApi.createEntity(entityData);
      await fetchData(); // Refresh data
      alert('Entidade criada com sucesso!');
    } catch (error) {
      throw error;
    }
  };

  const handleUpdateEntity = async (id, entityData) => {
    try {
      await adminApi.updateEntity(id, entityData);
      await fetchData(); // Refresh data
      alert('Entidade atualizada com sucesso!');
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteEntity = async (id) => {
    try {
      await adminApi.deleteEntity(id);
      await fetchData(); // Refresh data
      alert('Entidade removida com sucesso!');
    } catch (error) {
      throw error;
    }
  };

  // Ronda management handlers
  const handleCreateRonda = async (rondaData) => {
    try {
      await adminApi.createRonda(rondaData);
      await fetchData(); // Refresh data
      alert('Ronda criada com sucesso!');
    } catch (error) {
      throw error;
    }
  };

  const handleUpdateRonda = async (id, rondaData) => {
    try {
      await adminApi.updateRonda(id, rondaData);
      await fetchData(); // Refresh data
      alert('Ronda atualizada com sucesso!');
    } catch (error) {
      throw error;
    }
  };

  // Visited locations management handlers
  const handleCreateLocal = async (localData) => {
    try {
      await adminApi.createLocal(localData);
      await fetchData(); // Refresh data
      alert('Local criado com sucesso!');
    } catch (error) {
      throw error;
    }
  };

  const handleUpdateLocal = async (id, localData) => {
    try {
      await adminApi.updateLocal(id, localData);
      await fetchData(); // Refresh data
      alert('Local atualizado com sucesso!');
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteLocal = async (id) => {
    try {
      await adminApi.deleteLocal(id);
      await fetchData(); // Refresh data
      alert('Local eliminado com sucesso!');
    } catch (error) {
      throw error;
    }
  };

  // Admin management handlers (master admin only)
  const handleCreateAdmin = async (adminData) => {
    try {
      await adminApi.createAdmin(adminData);
      await fetchData(); // Refresh data
      alert('Administrador criado com sucesso!');
    } catch (error) {
      throw error;
    }
  };

  const handleUpdateAdmin = async (id, adminData) => {
    try {
      await adminApi.updateAdmin(id, adminData);
      await fetchData(); // Refresh data
      alert('Administrador atualizado com sucesso!');
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteAdmin = async (id) => {
    try {
      await adminApi.deleteAdmin(id);
      await fetchData(); // Refresh data
      alert('Administrador eliminado permanentemente!');
    } catch (error) {
      throw error;
    }
  };

  // Filter volunteers based on tab
  const getFilteredVolunteers = () => {
    if (!Array.isArray(volunteers)) {
      return [];
    }
    
    switch (activeTab) {
      case 'pending':
        return volunteers.filter(v => v.volunteer_status === 'pending');
      case 'approved':
        return volunteers.filter(v => v.volunteer_status === 'approved');
      case 'volunteers':
      default:
        return volunteers;
    }
  };

  const orgName = process.env.REACT_APP_ORG_NAME || 'ONG';

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
          <p className="mt-4 text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <LoginScreen onLoginSuccess={handleLoginSuccess} />;
  }

  // Show main app if authenticated
  return (
    <div className="app">
      <header className="header">
        <div className="header-content">
          <div>
            <h1>{orgName} - Painel Administrativo</h1>
            <p className="subtitle">
              Gestão de Voluntários e Rondas
            </p>
          </div>
          <div className="user-info">
            <div className="user-details">
              <span className="user-name">{currentUser?.nome}</span>
              <span className="user-role">
                ({currentUser?.tipo === 'mestre' ? 'Admin Mestre' : 'Admin Entidades'})
                {currentUser?.entidades && currentUser?.entidades.length > 0 && currentUser?.tipo === 'entidade' &&
                  ` - ${currentUser.entidades.map(e => e.id).join(', ')}`
                }
              </span>
              {currentUser?.entidades && currentUser?.entidades.length > 0 && currentUser.tipo === 'entidade' && (
                <small className="organization-info">
                  Entidades: {currentUser.entidades.map(e => e.nome).join(', ')}
                </small>
              )}
            </div>
            <button
              onClick={handleLogout}
              className="btn btn-outline btn-sm"
            >
              Sair
            </button>
          </div>
        </div>
      </header>

      <div className="container">
        <nav className="tabs">
          <button 
            className={`tab ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            Dashboard
          </button>
          <button 
            className={`tab ${activeTab === 'volunteers' ? 'active' : ''}`}
            onClick={() => setActiveTab('volunteers')}
          >
            Todos os Voluntários ({volunteers?.length || 0})
          </button>
          <button 
            className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            Pendentes ({Array.isArray(volunteers) ? volunteers.filter(v => v.volunteer_status === 'pending').length : 0})
          </button>
          <button 
            className={`tab ${activeTab === 'approved' ? 'active' : ''}`}
            onClick={() => setActiveTab('approved')}
          >
            Aprovados ({Array.isArray(volunteers) ? volunteers.filter(v => v.volunteer_status === 'approved').length : 0})
          </button>
          <button 
            className={`tab ${activeTab === 'entities' ? 'active' : ''}`}
            onClick={() => setActiveTab('entities')}
          >
            Entidades ({entities?.length || 0})
          </button>
          <button
            className={`tab ${activeTab === 'rondas' ? 'active' : ''}`}
            onClick={() => setActiveTab('rondas')}
          >
            Rondas ({rondas?.length || 0})
          </button>
          <button
            className={`tab ${activeTab === 'locais' ? 'active' : ''}`}
            onClick={() => setActiveTab('locais')}
          >
            Locais ({locais?.length || 0})
          </button>
          {currentUser?.tipo === 'mestre' && (
            <button 
              className={`tab ${activeTab === 'admins' ? 'active' : ''}`}
              onClick={() => setActiveTab('admins')}
            >
              Administradores ({admins?.length || 0})
            </button>
          )}
          <button 
            className={`tab ${activeTab === 'events' ? 'active' : ''}`}
            onClick={() => setActiveTab('events')}
          >
            Eventos
          </button>
        </nav>

        <main>
          {activeTab === 'dashboard' && (
            <Dashboard 
              stats={stats}
              loading={loading}
              error={error}
              onRefresh={fetchData}
              currentUser={currentUser}
            />
          )}

          {(['volunteers', 'pending', 'approved'].includes(activeTab)) && (
            <div className="card">
              <div className="card-header">
                <h3>
                  {activeTab === 'volunteers' && 'Todos os Voluntários'}
                  {activeTab === 'pending' && 'Voluntários Pendentes'}
                  {activeTab === 'approved' && 'Voluntários Aprovados'}
                </h3>
                <div>
                  <button className="btn btn-primary btn-sm" onClick={fetchData}>
                    Atualizar
                  </button>
                </div>
              </div>
              
              <div className="card-content">
                {loading ? (
                  <div className="loading">Carregando voluntários...</div>
                ) : error ? (
                  <div className="error">
                    <h3>Erro ao carregar dados</h3>
                    <p>{error}</p>
                    <button className="btn btn-primary" onClick={fetchData}>
                      Tentar novamente
                    </button>
                  </div>
                ) : (
                  <VolunteerTable
                    volunteers={getFilteredVolunteers()}
                    entities={entities}
                    onApprove={handleApproveVolunteer}
                    onUpdateStatus={handleUpdateVolunteerStatus}
                    setIsEditing={setIsEditing}
                  />
                )}
              </div>
            </div>
          )}


          {activeTab === 'entities' && (
            <div className="card">
              {loading ? (
                <div className="loading">Carregando entidades...</div>
              ) : error ? (
                <div className="error">
                  <h3>Erro ao carregar dados</h3>
                  <p>{error}</p>
                  <button className="btn btn-primary" onClick={fetchData}>
                    Tentar novamente
                  </button>
                </div>
              ) : (
                <EntityManagement
                  entities={entities}
                  onCreateEntity={handleCreateEntity}
                  onUpdateEntity={handleUpdateEntity}
                  onDeleteEntity={handleDeleteEntity}
                  setIsEditing={setIsEditing}
                />
              )}
            </div>
          )}

          {activeTab === 'rondas' && (
            <div className="card">
              {loading ? (
                <div className="loading">Carregando rondas...</div>
              ) : error ? (
                <div className="error">
                  <h3>Erro ao carregar dados</h3>
                  <p>{error}</p>
                  <button className="btn btn-primary" onClick={fetchData}>
                    Tentar novamente
                  </button>
                </div>
              ) : (
                <RondasManagement
                  rondas={rondas}
                  entities={entities}
                  volunteers={volunteers}
                  onCreateRonda={handleCreateRonda}
                  onUpdateRonda={handleUpdateRonda}
                  setIsEditing={setIsEditing}
                />
              )}
            </div>
          )}

          {activeTab === 'locais' && (
            <div className="card">
              {loading ? (
                <div className="loading">Carregando locais...</div>
              ) : error ? (
                <div className="error">
                  <h3>Erro ao carregar dados</h3>
                  <p>{error}</p>
                  <button className="btn btn-primary" onClick={fetchData}>
                    Tentar novamente
                  </button>
                </div>
              ) : (
                <LocaisManagement
                  locais={locais}
                  onCreateLocal={handleCreateLocal}
                  onUpdateLocal={handleUpdateLocal}
                  onDeleteLocal={handleDeleteLocal}
                  setIsEditing={setIsEditing}
                />
              )}
            </div>
          )}

          {activeTab === 'admins' && currentUser?.tipo === 'mestre' && (
            <div className="card">
              {loading ? (
                <div className="loading">Carregando administradores...</div>
              ) : error ? (
                <div className="error">
                  <h3>Erro ao carregar dados</h3>
                  <p>{error}</p>
                  <button className="btn btn-primary" onClick={fetchData}>
                    Tentar novamente
                  </button>
                </div>
              ) : (
                <AdminManagement
                  admins={admins}
                  entities={entities}
                  currentUser={currentUser}
                  onCreateAdmin={handleCreateAdmin}
                  onUpdateAdmin={handleUpdateAdmin}
                  onDeleteAdmin={handleDeleteAdmin}
                  setIsEditing={setIsEditing}
                />
              )}
            </div>
          )}

          {activeTab === 'events' && (
            <EventsDashboard />
          )}
        </main>
      </div>
    </div>
  );
}

export default App;